import { $isCodeNode } from "@lexical/code-core";
import { $isLinkNode } from "@lexical/link";
import { $findMatchingParent } from "@lexical/utils";
import { $getSelection, $isRangeSelection, $isTextNode } from "lexical";
import { BOUNDARY, type TriggerRule } from "./trigger";

export type OpenRefusal = "not-offered" | "invalid-target";

/**
 * Types a trigger where the caret is, the way a person would, so the extension's own listener
 * finds it and opens the menu. A host's "@" button cannot see what is before the caret, and a
 * trigger right after a word does not count (`a@b.com`), so the space a person would have typed
 * first is added here. A selection is left alone: the trigger goes after it. Must run inside an
 * update.
 */
export function $typeTrigger(
  trigger: string,
  rules: readonly TriggerRule[],
): { ok: true } | { ok: false; reason: OpenRefusal } {
  const rule = rules.find((candidate) => candidate.trigger === trigger);
  if (rule === undefined) return { ok: false, reason: "not-offered" };
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) return { ok: false, reason: "invalid-target" };
  if (!selection.isCollapsed()) {
    const end = selection.isBackward() ? selection.anchor : selection.focus;
    selection.anchor.set(end.key, end.offset, end.type);
    selection.focus.set(end.key, end.offset, end.type);
  }
  const node = selection.anchor.getNode();
  // Where the listener will not look for a trigger, typing one would only leave a stray character.
  const literal = $findMatchingParent(node, (parent) => $isCodeNode(parent) || $isLinkNode(parent));
  if (literal !== null || selection.hasFormat("code"))
    return { ok: false, reason: "invalid-target" };
  const offset = selection.anchor.offset;
  const inText = $isTextNode(node) && offset > 0;
  // An element point counts children, so zero is the block's start there too.
  const atBlockStart = offset === 0 && (!$isTextNode(node) || node.getPreviousSibling() === null);
  if (rule.blockStartOnly && !atBlockStart) return { ok: false, reason: "invalid-target" };
  const before = inText ? node.getTextContent()[offset - 1]! : "";
  selection.insertText(before === "" || BOUNDARY.test(before) ? trigger : ` ${trigger}`);
  return { ok: true };
}
